import { GetCountryRegexResponseType } from "@whatslly/types/GetCountryRegexResponse";
import { LoginProviderType } from "@whatslly/types/LoginProviderType";
import { UserType } from "@whatslly/types/User";
import { ActiveAction } from "@whatslly/types/integrations/appsActions/AppsActions";
import { WhatsappSelectorsType } from "@whatslly/types/integrations/im/WhatsappGetSelectorsResponse";
import { TelegramSelectorsType } from "@whatslly/types/integrations/im/TelegramGetSelectorsResponse";
import { AppsNamesType } from "@whatslly/types/AppsNames";
import { QueryResponseType } from "@whatslly/types/QueryResponse";
import { DLPPolicy } from "@whatslly/types/dlp/DLPPolicy";
import { IMChannelType } from "@whatslly/types/channelTypes";
import { DLPSettings } from "@whatslly/types/dlp/DLPSettings";
import { UrlPoliciesSettings } from "@whatslly/types/integrations/UrlPoliciesSettings";
import { BrowserUrlPoliciesSettings } from "@whatslly/types/integrations/BrowserUrlPolicy";
import { BrowserSettingsPoliciesSettings } from "@whatslly/types/integrations/BrowserSettings";
import { ApplicationsCatalog } from "@whatslly/types/integrations/Applications";
import { StartPageConfig, BrandingLayout } from "@whatslly/types/integrations/StartPage";
import { BlockPageLayout } from "@whatslly/types/integrations/BlockPage";
import { KeywordInsightsSettings } from "@whatslly/types/keyword-insights/KeywordInsightsSettings";
import { AiConversationLocalHistory } from "@whatslly/types/tuvis-agent/messages";
import { SurveillanceSettings } from "@whatslly/types/surveillance/SurveillancePolicy";
import { PostureSettings, PostureStatus } from "@whatslly/types/integrations/DevicePosture";
import { SaasStatusMap } from "@whatslly/types/integrations/SiteInfo";

export const STORAGE = {
  REGEX_KEY: "regex",
  ETOKEN_KEY: "etoken",
  POD_KEY: "whatsllyPod",
  APP_ACTIVE_KEY: "whatslly_app_active",
  CHAT_NOW_HINT_KEY: "whatslly_chat_now_hint",
  PENDING_WHATSAPP_PHONE: "whatslly_pending_whatsapp_phone",
  USER_ID_STORAGE_KEY: "whatslly_analytics_userId",
  ORG_ID_STORAGE_KEY: "whatslly_analytics_orgId",
  CODES_KEY: "codes",
  COUNTRY_CODES_KEY: "country_codes",
  ACTIVE_APPS: "active_apps",
  ACTIVE_APPS_LIST: "active_apps_list",
  BROWSER_ID: "browser-id",
  LAST_LOGGED_IN_USER: "last-user-phone",
  WAP_SELECTORS: "wap-selectors",
  TELEGRAM_SELECTORS: "telegram-selectors",
  LOGIN_PROVIDER: "login-provider",
  IS_VALID_BROWSER_VERSION: "isValidBrowserVersion",
  SEEN_INFORMATIONAL_MESSAGE_ID: "seen-informational-message-id",
  SFDC_SETTINGS: "sfdcSettings",
  PLATFORM: "platform",
  LOGIN_ACCESS: "login-access",
  RECENTLY_PERSON_SEARCH: "recently-person-search",
  FAVORITE_ACTIONS: "favorite-actions",
  USER: "user",
  ANALYTICS_EVENTS: "analytics-events",
  FAVORITE_TEMPLATES: "favorite-templates",
  NEW_FEATURE_TOOLTIP_DONE_ARRAY: "new-feature-tooltip-done-steps",
  BLUR_MODE_STATE: "blur-mode-state",
  LIMIT_NUMBER_OF_CHANNELS: "limit-number-of-channels",
  DESKTOP_LITE: "desktop-lite",
  DISABLE_SAVE_AS_AT_DESKTOP: "DISABLE_SAVE_AS_AT_DESKTOP",
  ELITE_SECURITY_TOKEN: "elite-security-token",
  DELETE_CRM_SESSION_ON_LOGOUT: "DELETE_CRM_SESSION_ON_LOGOUT",
  NOTIFICATION_ID: "whatslly_notification_id",
  ATTEMPTED_INTEGRATION: "attempted-integration",
  BASE_URL: "base_url",
  CHANNELS: "channels",
  ELITE_SECURITY_TOKEN_USER_AGENT_HEADER: "elite-security-token-user-agent-header",
  DEBUG_MODE: "DEBUG_MODE",
  ZOOM_FACTOR: "ZOOM_FACTOR",
  URL_WHITE_LIST: "URL_WHITE_LIST",
  DLP_RULES: "dlpRules", // for backward compatibility. can remove in a few versions of desktop & extension
  DLP_SETTINGS: "dlpSettings",
  SURVEILLANCE_SETTINGS: "surveillanceSettings",
  KEYWORD_INSIGHTS_SETTINGS: "keywordInsightsSettings",
  URL_POLICIES_SETTINGS: "urlPoliciesSettings",
  BROWSER_URL_POLICIES_SETTINGS: "browserUrlPoliciesSettings",
  BROWSER_SETTINGS: "browserSettings",
  LAST_PERIODIC_SYNC: "last-periodic-sync",
  ENGAGED_CONTACTS_MAP: "engaged_contacts_map",
  MOBILE_CURRENT_PHONE_NUMBER_SID: "mobile_current_phone_number_sid",
  MOBILE_CURRENT_PHONE_NUMBER: "mobile_current_phone_number",
  TWILIO_JWT_TOKEN: "twilio_jwt_token",
  USER_LAST_DLP_POLICIES_UPDATE: "user_last_dlp_policies_update",
  USER_LAST_SURVEILLANCE_POLICIES_UPDATE: "user_last_surveillance_policies_update",
  USER_LAST_KEYWORD_CAMPAIGNS_UPDATE: "user_last_keyword_campaigns_update",
  USER_LAST_SETTINGS_UPDATE: "user_last_settings_update",
  USER_URL_POLICIES_SETTINGS_UPDATE: "user_last_url_settings_update",
  USER_BROWSER_URL_POLICIES_SETTINGS_UPDATE: "user_last_browser_url_settings_update",
  USER_PHONE: "user_phone",
  DEFAULT_DIALER_ASKED: "default_dialer_asked",
  DATA_DISCLOSURE_ACCEPTED: "dataDisclosureAccepted",
  TELEGRAM_LINKED_SESSION: "telegram_linked_session",
  GROUP_EVENT_LAST_SYNC: "group-event-last-sync",
  APPLICATIONS_CATALOG_SETTINGS: "applicationsCatalogSettings",
  STARTPAGE_SETTINGS: "startPageSettings",
  BLOCK_PAGE_SETTINGS: "blockPageSettings",
  BRANDING_SETTINGS: "brandingSettings",
  POSTURE_SETTINGS: "postureSettings",
  POSTURE_FAILED_CHECKS: "postureFailedChecks",
  POSTURE_STATUS: "postureStatus",
  DEVICE_ID: "tuvisDeviceId",
  LOGIN_RESTRICTION_ARMED: "loginRestrictionArmed",
  ACCOUNT_STATE: "accountState",
  SAAS_STATUSES: "saasStatuses",
  SESSION_LIVENESS: "sessionLiveness",
  FORK_ETOKEN: "forkEtoken",
} as const;

export const STORAGE_COLLECTIONS = {
  QUERY_CACHE: "QUERY_CACHE",
  AI_CHAT_HISTORY: "AI_CHAT_HISTORY",
} as const;

type activeApps = {
  [key in `${typeof STORAGE.ACTIVE_APPS}:${string}`]: Partial<ActiveAction>;
};

type BaseStorageTypes = {
  [STORAGE.REGEX_KEY]: string;
  [STORAGE.ETOKEN_KEY]: string;
  [STORAGE.POD_KEY]: string;
  [STORAGE.APP_ACTIVE_KEY]: boolean;
  [STORAGE.CHAT_NOW_HINT_KEY]: string;
  [STORAGE.PENDING_WHATSAPP_PHONE]: string;
  [STORAGE.USER_ID_STORAGE_KEY]: string;
  [STORAGE.ORG_ID_STORAGE_KEY]: string;
  [STORAGE.CODES_KEY]: Array<[string, string]>;
  [STORAGE.COUNTRY_CODES_KEY]: GetCountryRegexResponseType & { success: true };
  [STORAGE.ACTIVE_APPS_LIST]: AppsNamesType[] | undefined;
  [STORAGE.BROWSER_ID]: string;
  [STORAGE.LAST_LOGGED_IN_USER]: string;
  [STORAGE.WAP_SELECTORS]: Partial<Record<IMChannelType, { selectors: WhatsappSelectorsType; ts: number }>>;
  [STORAGE.TELEGRAM_SELECTORS]: Partial<Record<IMChannelType, { selectors: TelegramSelectorsType; ts: number }>>;
  [STORAGE.LOGIN_PROVIDER]: LoginProviderType;
  [STORAGE.IS_VALID_BROWSER_VERSION]: boolean;
  [STORAGE.SEEN_INFORMATIONAL_MESSAGE_ID]: string;
  [STORAGE.SFDC_SETTINGS]: never;
  [STORAGE.DLP_RULES]: DLPPolicy[];
  [STORAGE.DLP_SETTINGS]: DLPSettings;
  [STORAGE.SURVEILLANCE_SETTINGS]: SurveillanceSettings;
  [STORAGE.KEYWORD_INSIGHTS_SETTINGS]: KeywordInsightsSettings;
  [STORAGE.URL_POLICIES_SETTINGS]: UrlPoliciesSettings;
  [STORAGE.BROWSER_URL_POLICIES_SETTINGS]: BrowserUrlPoliciesSettings;
  [STORAGE.BROWSER_SETTINGS]: BrowserSettingsPoliciesSettings;
  [STORAGE.PLATFORM]: string;
  [STORAGE.LOGIN_ACCESS]: number;
  [STORAGE.RECENTLY_PERSON_SEARCH]: string[];
  [STORAGE.FAVORITE_ACTIONS]: string[];
  [STORAGE.USER]: UserType;
  [STORAGE.ANALYTICS_EVENTS]: any;
  [STORAGE.FAVORITE_TEMPLATES]: string[];
  [STORAGE.NEW_FEATURE_TOOLTIP_DONE_ARRAY]: string[];
  [STORAGE.BLUR_MODE_STATE]: boolean;
  [STORAGE.LIMIT_NUMBER_OF_CHANNELS]: number;
  [STORAGE.DESKTOP_LITE]: boolean;
  [STORAGE.ELITE_SECURITY_TOKEN]: string;
  [STORAGE.DELETE_CRM_SESSION_ON_LOGOUT]: boolean;
  [STORAGE.NOTIFICATION_ID]: string;
  [STORAGE.ATTEMPTED_INTEGRATION]: string;
  [STORAGE.BASE_URL]: string;
  [STORAGE.CHANNELS]: any[];
  [STORAGE.ELITE_SECURITY_TOKEN_USER_AGENT_HEADER]: boolean;
  [STORAGE.DEBUG_MODE]: boolean;
  [STORAGE.ZOOM_FACTOR]: number;
  [STORAGE.URL_WHITE_LIST]: { urlWhitelist: string[]; ts: number };
  [STORAGE.ENGAGED_CONTACTS_MAP]: Record<string, string[]>;
  [STORAGE.LAST_PERIODIC_SYNC]: number;
  [STORAGE.GROUP_EVENT_LAST_SYNC]: Record<string, number>;
  [STORAGE.MOBILE_CURRENT_PHONE_NUMBER_SID]: string;
  [STORAGE.MOBILE_CURRENT_PHONE_NUMBER]: string;
  [STORAGE.TWILIO_JWT_TOKEN]: string;
  [STORAGE.USER_LAST_DLP_POLICIES_UPDATE]: number;
  [STORAGE.USER_LAST_SURVEILLANCE_POLICIES_UPDATE]: number;
  [STORAGE.USER_LAST_KEYWORD_CAMPAIGNS_UPDATE]: number;
  [STORAGE.USER_LAST_SETTINGS_UPDATE]: number;
  [STORAGE.USER_URL_POLICIES_SETTINGS_UPDATE]: number;
  [STORAGE.USER_BROWSER_URL_POLICIES_SETTINGS_UPDATE]: number;
  [STORAGE.DISABLE_SAVE_AS_AT_DESKTOP]: boolean;
  [STORAGE.USER_PHONE]: string;
  [STORAGE.DEFAULT_DIALER_ASKED]: string;
  [STORAGE.DATA_DISCLOSURE_ACCEPTED]: boolean;
  [STORAGE.TELEGRAM_LINKED_SESSION]: string | null;
  [STORAGE.APPLICATIONS_CATALOG_SETTINGS]: ApplicationsCatalog;
  [STORAGE.STARTPAGE_SETTINGS]: StartPageConfig;
  [STORAGE.BLOCK_PAGE_SETTINGS]: { blockPages: BlockPageLayout[] };
  [STORAGE.BRANDING_SETTINGS]: { brandings: BrandingLayout[] };
  [STORAGE.POSTURE_SETTINGS]: PostureSettings;
  [STORAGE.POSTURE_FAILED_CHECKS]: string[];
  [STORAGE.POSTURE_STATUS]: PostureStatus | null;
  [STORAGE.DEVICE_ID]: string;
  [STORAGE.LOGIN_RESTRICTION_ARMED]: string[];
  [STORAGE.ACCOUNT_STATE]: Record<
    string,
    { accountType: "corporate" | "personal" | "unknown"; accountDomain?: string; at: number; status: "ok" | "no_match" | "error" }
  >;
  [STORAGE.SAAS_STATUSES]: SaasStatusMap;
  [STORAGE.SESSION_LIVENESS]: { failures: number };
  [STORAGE.FORK_ETOKEN]: string;
};

type StorageCollectionTypes = {
  [STORAGE_COLLECTIONS.QUERY_CACHE]: { timestamp: number; query: QueryResponseType };
  [STORAGE_COLLECTIONS.AI_CHAT_HISTORY]: AiConversationLocalHistory;
};

export type StorageTypes = BaseStorageTypes & activeApps;

export type StorageKey = keyof StorageTypes;
export type StorageValue<T extends StorageKey> = StorageTypes[T];

export type StorageCollection = keyof StorageCollectionTypes;
export type StorageCollectionValue<T extends StorageCollection> = StorageCollectionTypes[T];

export const InjectStorage = Symbol.for("Storage");

export interface IStorage {
  get<K extends StorageKey>(key: K): Promise<StorageValue<K> | null>;
  get<K extends StorageKey[]>(key: K): Promise<Pick<StorageTypes, K[number]>>;
  getIndexed<K extends StorageCollection>(collection: K, key: string): Promise<StorageCollectionValue<K> | undefined>;
  getAll?(): Promise<Partial<StorageTypes>>;
  set<K extends StorageKey | Partial<StorageTypes>>(key: K, value: K extends StorageKey ? StorageValue<K> : never): Promise<void>;
  set(data: Partial<StorageTypes>): Promise<void>;
  setIndexed<K extends StorageCollection>(collection: K, key: string, value: StorageCollectionValue<K>): Promise<void>;
  remove<K extends StorageKey | StorageKey[]>(key: K): Promise<void>;
  clear(options?: { excludeCollections?: StorageCollection[] }): Promise<void>;
  onChange?<K extends StorageKey>(key: K, cb: () => void): void;
}
